import { useAppDispatch, useAppSelector } from 'hooks/redux.hooks';
import { useEffect, useMemo, useState } from 'react';
import { selectTranslations } from 'state/reducers/translation.reducers';
import { useNavigate, useParams } from 'react-router-dom';
import { getAltshulerEmployees } from 'state/reducers/company.reducers';

import { fieldsGroupType, fieldType } from 'components/FormCreatorComponent/types';
import FormCreatorComponent from 'components/FormCreatorComponent';
import { DashboardCategories, IFilterType } from 'constants/balance.constants';
import { format, startOfYesterday } from 'date-fns';
import { getAccountBalances, getPendingBalances } from 'services/balances.service';
import { setFilters } from 'state/reducers/dashboard.reducer';
import _ from 'lodash';
import { getCompanies } from 'state/reducers/global.reducer';
import { getUserDetails } from 'state/reducers/user.reducers';

export const FilterComponent = () => {
	const dispatch = useAppDispatch();
	const t = useAppSelector(selectTranslations);
	const employees = useAppSelector(getAltshulerEmployees(true));
	const navigate = useNavigate();
	const filters = useAppSelector((state) => state.dashboard.filters);
	const { accountBalance } = useAppSelector((state) => state.balance);
	const { category } = useParams();
	const companies = useAppSelector(getCompanies);

	useEffect(() => {
		if (accountBalance === null) {
			dispatch(getAccountBalances(filters, false)); //load from filter component
		}
	}, [accountBalance]);

	const currencyOptions = useMemo(() => {
		const options: any[] = [];
		accountBalance?.forEach((currency: any) => {
			options.push({
				value: currency?.currencyId,
				label: currency?.currencyName,
			});
		});
		return options;
	}, [accountBalance]);

	const selectCurrencyField = {
		type: fieldType.select,
		props: {
			name: 'currency',
			label: t('page.customerAccounts.basicData.currency'),
			required: false,
			disabled: false,
			options: currencyOptions,
		},
	};

	const dateRangeLabel = t('report.reportDate');
	const dateRangeField = {
		type: fieldType.dateRangePicker,
		months: 2,
		props: {
			required: false,
			name: 'datesRange',
			label: dateRangeLabel,
			placeholder: dateRangeLabel,
		},
	};

	const selectMangerField = {
		type: fieldType.select,
		props: {
			name: 'manager',
			label: t('page.customerAccounts.basicData.manager'),
			required: false,
			disabled: false,
			options: employees.altshulerManagers,
		},
	};

	const selectLeadField = {
		type: fieldType.select,
		props: {
			name: 'lead',
			label: t('page.customerAccounts.basicData.lead'),
			required: false,
			disabled: false,
			options: employees.altshulerLeads,
		},
	};

	const selectDealerField = {
		type: fieldType.select,
		props: {
			name: 'dealer',
			label: t('page.customerAccounts.basicData.dealer'),
			required: false,
			disabled: false,
			options: employees.altshulerDealers,
		},
	};

	const selectConversionType = {
		type: fieldType.select,
		props: {
			name: 'dealType',
			label: t('page.dashboard.dashboardBalances.table.headers.dealType'),
			required: false,
			disabled: false,
			options: [
				{ value: 'sell', label: t('page.dashboard.dashboardBalances.table.headers.ccSellAmount') },
				{ value: 'buy', label: t('page.dashboard.dashboardBalances.table.headers.ccBuyAmount') },
			],
		},
	};

	const selectPaymentType = {
		type: fieldType.select,
		props: {
			name: 'paymentType',
			label: t('page.customerAccounts.tables.beneficiaries.filters.paymentType.label'),
			required: false,
			disabled: false,
			options: [
				{
					value: 'priority',
					label: t('page.customerAccounts.tables.beneficiaries.filters.paymentType.priority'),
				},
				{ value: 'regular', label: t('page.customerAccounts.tables.beneficiaries.filters.paymentType.local') },
			],
		},
	};

	const selectCustomer = {
		type: fieldType.select,
		props: {
			name: 'company',
			label: t('page.dashboard.dashboardBalances.table.headers.customerName'),
			required: false,
			options: companies,
		},
	};

	const getListFilters = useMemo(() => {
		switch (category) {
			case DashboardCategories.CUSTOMER_BALANCES:
			case DashboardCategories.PENDING_BALANCE:
			case DashboardCategories.CUSTOMER_BALANCES_DETAIL_BY_CUSTOMERS:
				return [selectMangerField, selectCurrencyField, dateRangeField];
			case DashboardCategories.CONVERSION_FEES:
				return [selectMangerField, selectConversionType, selectCurrencyField, selectCustomer];
			case DashboardCategories.PAYMENT_FEES:
				return [selectMangerField, selectCurrencyField, selectPaymentType];
			case DashboardCategories.MONTHLY_SUBSCRIPTIONS:
				return [selectMangerField, selectLeadField, selectDealerField];
			case DashboardCategories.MONTHLY_SUBSCRIPTIONS_BY_CUSTOMERS:
				return [selectMangerField, selectLeadField, selectDealerField];
			case DashboardCategories.TOTAL_REVENUE:
				return [selectMangerField, selectLeadField, selectDealerField];
			default:
				return [selectMangerField, selectLeadField, selectDealerField, dateRangeField];
		}
	}, [category, employees, accountBalance]);
	useEffect(() => {
		dispatch(getAccountBalances(filters, false)); //load from filter component
	}, []);

	useEffect(() => {
		//reset filter
		const all: string[] = [
			'manager',
			'lead',
			'dealer',
			'currency',
			'company',
			'paymentType',
			'dealType',
			'datesRange',
		];
		let list: string[] = [];
		switch (category) {
			case DashboardCategories.CUSTOMER_BALANCES:
			case DashboardCategories.PENDING_BALANCE:
			case DashboardCategories.CUSTOMER_BALANCES_DETAIL_BY_CUSTOMERS:
				list = ['manager', 'currency', 'datesRange'];
				break;
			case DashboardCategories.CONVERSION_FEES:
				list = ['manager', 'dealType', 'currency', 'company'];
				break;
			case DashboardCategories.PAYMENT_FEES:
				list = ['manager', 'currency', 'paymentType'];
				break;
			case DashboardCategories.MONTHLY_SUBSCRIPTIONS:
				list = ['manager', 'lead', 'dealer'];
				break;
			case DashboardCategories.TOTAL_REVENUE:
				list = ['manager', 'lead', 'dealer'];
				break;
			default:
				list = ['manager', 'lead', 'dealer', 'datesRange'];
		}
		const _filters: object = { ...filters };
		_.difference(all, list).forEach((filter) => filter !== 'datesRange' && _.assign(_filters, { [filter]: '' }));
		if (!_.isEqual(_filters, filters)) {
			dispatch(setFilters(_filters));
		}
	}, [category, filters]);

	return (
		<FormCreatorComponent
			initialValidation={{}}
			name="report"
			handleChange={(formState: any, field: any) => {
				if (formState?.values) {
					const updatedValues = { ...formState.values };
					const fieldsToCheck = ['manager', 'dealer', 'lead', 'currency'];
					fieldsToCheck.forEach((fieldName) => {
						if (
							typeof updatedValues[fieldName] === 'object' ||
							updatedValues[fieldName] === null ||
							updatedValues[fieldName] === undefined
						) {
							updatedValues[fieldName] = '';
						}
					});
					dispatch(setFilters(updatedValues));
				}
			}}
			values={filters}
			fieldsGroups={[
				{
					type: fieldsGroupType.row,
					fields: getListFilters, //[selectMangerField, selectLeadField, selectDealerField, dateRangeField, selectCurrencyField],
				},
			]}
		/>
	);
};
